// Single source of truth for all DealShaq legal documents.
//
// Each document is stored as a markdown-flavoured string so the same content
// powers the public web pages (LegalPage), the pilot-acceptance modals, and
// (eventually) PDF exports for retailer signatures.
//
// To update a document:
//   1. Bump the `effectiveDate` string.
//   2. Edit the body.
//   3. Optionally bump the `version` integer — Phase 2 will compare this
//      against the user's stored acceptance to re-prompt.

export const ENTITY_NAME = 'DealShaq Technologies, Inc.';
export const ENTITY_ADDRESS = '11654 Plaza America Drive, #366, Reston, VA 20190 U.S.A.';
export const SUPPORT_EMAIL = 'support@dealshaq.com';

export const LEGAL_DOCS = {
  'consumer-terms': {
    id: 'consumer-terms',
    title: 'Consumer Terms of Service',
    slug: 'terms',
    version: 2,
    effectiveDate: 'August 3, 2026',
    body: `These Consumer Terms of Service ("Terms") govern your use of DealShaq's website, mobile applications, and related services ("Services"). By using the Services, you agree to these Terms.

"DealShaq," "we," "us," and "our" refer to:

${ENTITY_NAME}

## 1. Overview of the Services

DealShaq connects consumers with participating grocery retailers ("Retailers") offering discounted items. DealShaq displays deals, sets pricing for items offered through the Services, processes payments, coordinates pickup or delivery, handles all customer support, handles all refunds, and handles all recall notifications. Retailers do not provide customer service for DealShaq orders.

## 2. Eligibility and Account Requirements

You must be at least 18 years old to use the Services. You agree to provide accurate information, maintain account security, not share your account, and not misuse the Services.

DealShaq does not knowingly collect information from individuals under 18. If you believe a minor has created an account, please contact ${SUPPORT_EMAIL} to request deletion.

## 3. Orders, Pricing, and Availability

Retailers provide product descriptions and inventory information. DealShaq controls pricing for items offered through the Services. DealShaq is not responsible for out-of-stock items or inaccurate descriptions provided by Retailers. If an item is unavailable, DealShaq may issue a refund or credit.

## 4. Refunds, Recalled Items, and Product Issues

This section applies only to consumers. Retailer obligations appear in the Retailer Agreement.

### 4.1 No Returns to Retailers

You must not return items to the Retailer, contact the Retailer for refunds, or attempt to exchange items at the store. Retailers are not authorized to assist you because DealShaq is the merchant of record for all orders placed through the Services.

### 4.2 How to Request a Refund

All refund requests must be submitted to DealShaq Support through the app or website. You must provide a clear photo of the item, a photo of the packaging, a photo of the expiration date if relevant, and a description of the issue. Lack of photo evidence may result in denial of the refund.

DealShaq will make reasonable accommodations for customers who cannot provide photo evidence.

### 4.3 Disposal Video (Soft Version)

DealShaq may request a disposal video to help verify refund requests. If you cannot provide one, DealShaq may request alternative evidence. You must ensure that no faces, voices, or personally identifying information appear in the video.

DealShaq will make reasonable accommodations for customers unable to provide video evidence.

### 4.4 Disposal Certification

By requesting a refund, you certify that you will not consume the item and will dispose of it safely.

### 4.5 Refund Abuse

DealShaq may deny refunds, limit refund frequency, or suspend accounts for repeated refund requests, suspicious patterns, or misrepresentation.

### 4.6 Recalled Items

If an item is recalled, do not consume it, do not return it to the store, and dispose of it safely. DealShaq will refund you.

### 4.7 Cross-Reference

See the Refund & Recall Policy for additional details.

## 5. Delivery, Pickup, and Third-Party Couriers

DealShaq uses third-party delivery partners to deliver your order. These couriers act on DealShaq's behalf, not the Retailer's. DealShaq remains responsible for coordinating and managing delivery. If a delivery issue occurs, you must contact DealShaq Support.

Once a courier confirms delivery to the address you provided, responsibility transfers to you. If you dispute a delivery confirmation, DealShaq will investigate and may request additional information.

## 6. Payments

Payments are processed by third-party providers. You authorize DealShaq to charge your payment method, issue refunds or credits, and resolve payment disputes. DealShaq does not store full payment card numbers.

Refunds may be issued to your original payment method or as DealShaq credits. Refund processing times vary and may take 3–10 business days depending on your payment provider.

## 7. Acceptable Use

You agree not to interfere with the Services, attempt to manipulate pricing, attempt to bypass refund rules, or harass Retailers or couriers.

You may not engage in fraudulent activity, submit false refund claims, attempt unauthorized access to the Services, or use automated tools to scrape or collect data.

## 8. Communications

### 8.1 SMS Messaging

If you choose to opt in to SMS notifications, you consent to receive text messages from DealShaq at the phone number you provide. These messages may include account alerts, order updates, delivery notifications, and customer-support communications.

Message frequency varies. Message and data rates may apply. You may opt out at any time by replying STOP to any message. Reply HELP for assistance or email ${SUPPORT_EMAIL}.

Your opt-in is voluntary and is not a condition of purchasing any DealShaq product or service.

Additional SMS terms and disclosures are available at dealshaq.com/sms-optin.

## 9. Intellectual Property

All content belongs to DealShaq or its partners. You may not copy, scrape, reproduce, or distribute content without permission.

## 10. Disclaimer of Warranties

The Services are provided "as is" and "as available."

## 11. Limitation of Liability

DealShaq's liability is limited to the amount you paid for the affected order.

This limitation does not apply where prohibited by law, including claims involving personal injury, food safety, or statutory consumer rights.

## 12. Dispute Resolution

**IMPORTANT: THIS SECTION CONTAINS AN ARBITRATION AGREEMENT AND CLASS ACTION WAIVER THAT AFFECT YOUR RIGHTS. PLEASE READ CAREFULLY.**

### 12.1 Informal Resolution

You must contact DealShaq Support and allow 30 days for resolution before initiating arbitration.

### 12.2 Arbitration Agreement

Most disputes must be resolved through binding arbitration.

### 12.3 30-Day Opt-Out

You may opt out within 30 days of creating your account by emailing ${SUPPORT_EMAIL}.

### 12.4 Class Action Waiver

You agree not to participate in class actions against DealShaq.

## 13. Changes to These Terms

We may update these Terms. The Effective Date indicates the latest version.

We will notify you of material changes via email or through the Services. Continued use of the Services after changes take effect constitutes acceptance of the updated Terms.

## 14. Contact

${ENTITY_NAME}

11654 Plaza America Drive, #366

Reston, VA 20190 U.S.A.

${SUPPORT_EMAIL}`,
  },

  'consumer-privacy': {
    id: 'consumer-privacy',
    title: 'Consumer Privacy Policy',
    slug: 'privacy',
    version: 2,
    effectiveDate: 'August 3, 2026',
    body: `This Privacy Policy explains how DealShaq collects, uses, shares, and protects your personal information. By using the Services, you agree to this Policy.

"DealShaq," "we," "us," and "our" refer to:

${ENTITY_NAME}

## 1. Information We Collect

### 1.1 Information You Provide

You may provide account information, payment information processed by third-party providers, order details, refund request materials including photos and descriptions, refund-verification videos if submitted, and communications with DealShaq Support.

### 1.2 Information Collected Automatically

We may collect device identifiers, IP address, browser type, app usage data, crash logs, location if enabled, and data collected through tracking technologies described in our Cookie Policy.

### 1.3 Information from Retailers and Couriers

We may receive order-fulfillment status, pickup or delivery confirmations, product availability information, and courier delivery data.

### 1.4 SMS Communications

If you opt in to receive SMS notifications, we collect and store your phone number, opt-in timestamp, IP address, and related metadata for compliance and messaging purposes. We use this information solely to deliver transactional messages such as order updates, delivery alerts, and account notifications.

You may opt out at any time by replying STOP. We do not share SMS consent data with third parties except as required to deliver messages through our telecommunications partners.

## 2. How We Use Your Information

We use your information to provide and improve the Services, process orders and payments, investigate refund requests, verify disposal videos, detect and prevent fraud, send recall notifications, and comply with legal obligations.

## 3. How We Share Your Information

We may share information with Retailers for order-related purposes, couriers for delivery-related purposes, payment processors, analytics providers, customer-support tools, and government authorities when required.

## 4. Your Rights

Depending on your location, you may have rights to access, correct, or delete your information, opt out of targeted advertising, or opt out of certain types of data sharing. Requests may be submitted to ${SUPPORT_EMAIL}.

## 5. Data Retention

Refund-verification videos are retained only as long as necessary for fraud prevention, claim resolution, and legal compliance.

## 6. Security

We use administrative, technical, and physical safeguards to protect your information. No system is completely secure, but we take reasonable steps to protect your data.

## 7. Children

The Services are not intended for individuals under 18, and we do not knowingly collect information from minors.

## 8. Changes

We may update this Policy periodically. The Effective Date indicates the latest version.

## 9. Contact

${ENTITY_NAME}

11654 Plaza America Drive, #366

Reston, VA 20190 U.S.A.

${SUPPORT_EMAIL}`,
  },

  'cookie-policy': {
    id: 'cookie-policy',
    title: 'Cookie & Tracking Technologies Policy',
    slug: 'cookies',
    version: 2,
    effectiveDate: 'August 3, 2026',
    body: `This Cookie & Tracking Technologies Policy explains how DealShaq uses cookies, SDKs, pixels, tags, and similar technologies ("Tracking Technologies") on our website and mobile applications. By using the Services, you agree to this Policy.

"DealShaq," "we," "us," and "our" refer to:

${ENTITY_NAME}

## 1. What Cookies and Tracking Technologies Are

Cookies are small text files stored on your browser. Mobile applications use similar technologies such as SDKs, device identifiers, and local storage. These technologies help us operate the Services, understand usage patterns, and improve performance.

## 2. Types of Tracking Technologies We Use

We may use essential technologies required for the Services to function, performance and analytics technologies to understand how users interact with the Services, functionality technologies to remember your preferences, and advertising or targeting technologies to deliver relevant content. Mobile applications may also use SDKs and device-level identifiers.

## 3. Third-Party Tracking

We may allow third-party partners, including analytics providers, advertising networks, payment processors, and delivery partners, to set or access Tracking Technologies when you use the Services. These partners may collect information about your device, browsing behavior, or interactions with the Services.

## 4. How You Can Control Tracking

You may control Tracking Technologies by using our Cookie Consent Manager, adjusting your browser settings, adjusting your mobile device's tracking settings, or opting out of targeted advertising where available. Some features of the Services may not function properly if certain technologies are disabled.

## 5. Updates

We may update this Policy periodically to reflect changes in technology, law, or our practices. The Effective Date indicates the latest version.

## 6. Contact

${ENTITY_NAME}

11654 Plaza America Drive, #366

Reston, VA 20190 U.S.A.

${SUPPORT_EMAIL}`,
  },

  'retailer-agreement': {
    id: 'retailer-agreement',
    title: 'Retailer Agreement',
    slug: 'retailer-agreement',
    version: 3,
    effectiveDate: 'August 6, 2026',
    body: `This Retailer Agreement ("Agreement") is entered into by and between DealShaq Technologies, Inc. ("DealShaq") and the participating retailer ("Retailer"). This Agreement governs Retailer's participation in the DealShaq marketplace. This Agreement uses a formal legal style, as it is a binding commercial contract.

"DealShaq," "we," "us," and "our" refer to:

${ENTITY_NAME}

## 1. Definitions

"Services" refers to DealShaq's website, mobile applications, and related technology.

"Retailer Content" refers to product data, pricing, images, descriptions, and inventory information provided by Retailer.

"Orders" refers to consumer purchases made through DealShaq.

"Refund & Recall Policy" refers to the policy incorporated by reference into this Agreement.

## 2. Retailer Responsibilities

### 2.1 Product Accuracy

Retailer shall ensure that all Retailer Content is accurate, including product descriptions, pricing, images, inventory availability, and expiration dates.

### 2.2 Order Fulfillment

Retailer shall prepare Orders promptly, ensure items match descriptions, ensure items meet all food safety and quality standards, and package items appropriately for pickup or delivery.

### 2.3 Food Safety

Retailer is solely responsible for compliance with all applicable food safety laws, storage requirements, temperature-control requirements, and labeling requirements.

## 3. Refunds, Returns, and Customer Interaction

This section governs Retailer's obligations regarding refunds and consumer interactions.

### 3.1 No Returns

Retailer shall not accept returns of any items purchased through DealShaq.

### 3.2 No Refunds

Retailer shall not issue refunds, credits, exchanges, or replacements for any DealShaq Orders.

### 3.3 No Consumer Contact

Retailer shall not communicate with consumers about refunds, direct consumers to return items, provide customer service for DealShaq Orders, or attempt to resolve consumer complaints directly. All consumer issues must be referred to DealShaq.

### 3.4 Cooperation with Refund Investigations

Retailer shall cooperate with DealShaq in refund investigations, including providing batch or lot numbers, expiration dates, inventory data, product information, and safety documentation.

### 3.5 Disposal-Video Verification (Soft Version)

DealShaq may request that consumers provide disposal videos to help verify refund requests. If DealShaq requests additional information from Retailer to support such investigations, Retailer shall provide it promptly.

### 3.6 Defect Liability & Settlement Deductions

Refund adjustments are routed by fault. Reasons attributable to Retailer (expired, spoiled, damaged, mislabeled, recall, missing item due to retailer error) result in the corresponding wholesale value being withheld from Retailer's next payout. Reasons attributable to the courier (delivery damage, courier-missing items, entire order undelivered) or to the consumer (changed mind, fraudulent claim) do not reduce Retailer's payout; DealShaq absorbs those costs, including any Stripe processor fee on refunded delivery fees.

### 3.7 Monthly Rollup Statement & 7-Day Dispute Window

DealShaq issues a monthly rollup statement summarizing all refund adjustments applied during the prior calendar month. Retailer has seven (7) calendar days from the date the rollup statement is issued to dispute any adjustment by submitting written comment and supporting evidence through the DealShaq Retailer portal. Adjustments not disputed within the seven-day window are deemed final. DealShaq will resolve disputes within a commercially reasonable time and may approve (reversing the withhold) or deny the dispute in its sole discretion.

## 4. Product Recalls

### 4.1 Retailer Obligations

Retailer shall notify DealShaq immediately upon learning of a recall, provide affected product lists, provide batch or lot information, and cooperate fully with recall investigations.

### 4.2 DealShaq Responsibilities

DealShaq will notify consumers, issue refunds or credits, and provide disposal instructions. Retailer shall not communicate directly with consumers regarding recalls.

## 5. Delivery

DealShaq is solely responsible for coordinating delivery of Orders using third-party couriers. Retailer has no responsibility for courier performance, delivery delays, delivery errors, or post-pickup issues. DealShaq will manage all consumer communications and delivery-related complaints.

## 6. Payments

DealShaq will remit payments to Retailer net of platform fees, refund adjustments, chargebacks, fraudulent claims as determined by DealShaq, and promotional discounts if applicable. Retailer acknowledges that DealShaq has sole discretion to resolve consumer refund requests.

### 6.1 Payout Cadence

Payouts are issued weekly. A monthly rollup statement (Section 3.7) consolidates the prior month's adjustments and opens the 7-day dispute window.

## 7. Compliance

Retailer shall comply with all applicable federal, state, and local laws, food safety regulations, health department rules, and recall requirements.

## 8. Indemnification

Retailer shall indemnify, defend, and hold harmless DealShaq from claims arising out of product defects, food safety violations, mislabeling, regulatory noncompliance, or Retailer's breach of this Agreement.

## 9. Limitation of Liability

DealShaq's total liability to Retailer is limited to the amount paid to Retailer for the affected Order. DealShaq is not liable for lost profits, indirect damages, or consequential damages.

## 10. Term and Termination

Either party may terminate this Agreement with 30 days' written notice. DealShaq may terminate immediately for fraud, safety violations, repeated consumer complaints, or regulatory violations.

## 11. Notices

All notices to DealShaq must be sent to:

${ENTITY_NAME}

11654 Plaza America Drive, #366

Reston, VA 20190 U.S.A.

${SUPPORT_EMAIL}

## 12. Entire Agreement

This Agreement, together with the Refund & Recall Policy, constitutes the entire agreement between the parties.`,
  },

  'retailer-pilot': {
    id: 'retailer-pilot',
    title: 'Retailer Pilot Agreement',
    slug: 'retailer-pilot-agreement',
    version: 2,
    effectiveDate: 'August 3, 2026',
    body: `This Retailer Pilot Agreement ("Agreement") governs Retailer's participation in the DealShaq pilot program. It is a simplified version of the Retailer Agreement.

"DealShaq," "we," "us," and "our" refer to:

${ENTITY_NAME}

## 1. Purpose

Retailer agrees to participate in the DealShaq pilot program to test the Services and operational workflows.

## 2. Retailer Responsibilities

Retailer shall provide accurate product data, maintain accurate pricing, ensure food safety compliance, prepare Orders promptly, and cooperate with DealShaq staff during the pilot.

## 3. Refunds, Returns, and Customer Interaction

### 3.1 No Returns

Retailer shall not accept returns of any DealShaq items.

### 3.2 No Refunds

Retailer shall not issue refunds, credits, or exchanges.

### 3.3 No Consumer Contact

Retailer shall not communicate with consumers regarding refunds or product issues.

### 3.4 Cooperation with Refund Investigations

Retailer shall provide requested product information, including batch or lot numbers, expiration dates, and inventory data.

### 3.5 Disposal-Video Verification (Soft Version)

DealShaq may request disposal videos from consumers to help verify refund requests. Retailer shall cooperate with any related information requests.

## 4. Recalls

Retailer shall notify DealShaq immediately of any recall, provide affected product lists, and cooperate with DealShaq investigations. DealShaq will notify consumers and issue refunds.

## 5. Delivery

DealShaq is solely responsible for coordinating delivery using third-party couriers. Retailer has no responsibility for delivery performance or post-pickup issues.

## 6. Payments

DealShaq will remit payments net of platform fees, refund adjustments, chargebacks, and fraudulent claims.

## 7. Termination

Either party may terminate the pilot at any time with written notice.

## 8. Notices

${ENTITY_NAME}

11654 Plaza America Drive, #366

Reston, VA 20190 U.S.A.

${SUPPORT_EMAIL}`,
  },

  'consumer-pilot': {
    id: 'consumer-pilot',
    title: 'Consumer Pilot Agreement',
    slug: 'consumer-pilot-agreement',
    version: 2,
    effectiveDate: 'August 3, 2026',
    body: `This Consumer Pilot Agreement ("Agreement") applies to consumers participating in the DealShaq pilot program. It supplements the Consumer Terms of Service and incorporates the Refund & Recall Policy. By participating in the pilot, you agree to follow this Agreement and the Consumer Terms of Service.

"DealShaq," "we," "us," and "our" refer to:

${ENTITY_NAME}

## 1. Purpose of the Pilot

The pilot program allows selected consumers to test DealShaq's early-stage features, workflows, and operational processes. The purpose of the pilot is to evaluate functionality, gather feedback, and improve the Services before broader release.

## 2. Eligibility

You must be at least 18 years old, have an active DealShaq account, and reside in an eligible pilot area. DealShaq may modify eligibility criteria at any time.

## 3. Refunds and Returns

This section summarizes the refund rules for pilot participants. Full details appear in the Refund & Recall Policy.

### 3.1 No Returns

You must not return items to the Retailer. Retailers cannot assist with refunds, exchanges, or product issues.

### 3.2 Refund Requests

All refund requests must be submitted to DealShaq Support. You must provide a photo of the item, a photo of the packaging, and a description of the issue. Additional evidence may be required depending on the nature of the request.

### 3.3 Disposal-Video Verification (Soft Version)

DealShaq may request a disposal video to help verify refund requests. If you cannot provide one, DealShaq may request alternative evidence. You must ensure that no faces, voices, or personally identifying information appear in the video.

### 3.4 Disposal Certification

By requesting a refund, you certify that you will not consume the item and will dispose of it safely.

## 4. Recalls

If an item is recalled, you must not consume it and must dispose of it safely. DealShaq will refund you. Retailers cannot assist with recall-related issues.

## 5. Delivery

DealShaq uses third-party couriers such as DoorDash to deliver orders. These couriers act on DealShaq's behalf. DealShaq remains responsible for coordinating and managing delivery, including investigating delivery issues and issuing refunds or credits when appropriate.

## 6. Feedback

You agree to provide feedback about your pilot experience. DealShaq may use this feedback for product improvement without compensation. Feedback may be collected through surveys, in-app prompts, or direct communication.

## 7. Termination

DealShaq may end the pilot at any time. You may withdraw at any time by discontinuing use of the pilot features. Termination does not affect your obligations regarding refunds, disposal certification, or compliance with this Agreement.

## 8. Contact

${ENTITY_NAME}

11654 Plaza America Drive, #366

Reston, VA 20190 U.S.A.

${SUPPORT_EMAIL}`,
  },

  'refund-recall': {
    id: 'refund-recall',
    title: 'Refund & Recall Policy',
    slug: 'refund-policy',
    version: 2,
    effectiveDate: 'August 3, 2026',
    body: `This Refund & Recall Policy ("Policy") applies to all DealShaq consumers and Retailers. It is incorporated into both the Consumer Terms of Service and the Retailer Agreement. This Policy explains how refunds, disposal requirements, and product recalls are handled across the DealShaq platform.

"DealShaq," "we," "us," and "our" refer to:

${ENTITY_NAME}

# SECTION A — CONSUMER RULES

## 1. No Returns

Consumers must not return items to Retailers. Retailers are not authorized to accept returns, exchanges, or replacements for any DealShaq orders.

## 2. No Contacting Retailers

Consumers must not contact Retailers regarding refunds, exchanges, or product issues. All refund-related communication must go through DealShaq Support.

## 3. Refund Requests

Consumers must submit refund requests to DealShaq Support. Refund requests must include a photo of the item, a photo of the packaging, a photo of the expiration date if relevant, and a description of the issue. DealShaq may request additional evidence depending on the nature of the claim.

## 4. Disposal-Video Verification (Soft Version)

DealShaq may request a disposal video to help verify refund requests. If the consumer cannot provide a disposal video, DealShaq may request alternative evidence. Consumers must ensure that no faces, voices, or personally identifying information appear in the video.

## 5. Disposal Certification

By requesting a refund, consumers certify that they will not consume the item and will dispose of it safely. This certification applies to all refund types, including quality issues, expiration-related issues, and recall-related issues.

## 6. Refund Abuse

DealShaq may deny refunds, limit refund frequency, or suspend accounts for repeated refund requests, suspicious patterns, or misrepresentation. DealShaq may also require additional verification for future refund requests if abuse is suspected.

## 7. Recalled Items

If an item is recalled, consumers must not consume it and must dispose of it safely. Consumers must not return recalled items to the store. DealShaq will refund the consumer for any recalled item. Retailers cannot assist with recall-related issues.

# SECTION B — RETAILER RULES

## 1. No Returns

Retailers must not accept returns of DealShaq items under any circumstances. Consumers must be directed to DealShaq Support for all issues.

## 2. No Refunds

Retailers must not issue refunds, credits, exchanges, or replacements for any DealShaq orders. DealShaq has sole authority to approve or deny refunds.

## 3. No Consumer Contact

Retailers must not communicate with consumers about refunds, direct consumers to return items, or attempt to resolve consumer complaints. All consumer issues must be referred to DealShaq.

## 4. Cooperation with Refund Investigations

Retailers must provide batch or lot numbers, expiration dates, inventory data, product information, and any other information reasonably requested by DealShaq to support refund investigations.

## 5. Disposal-Video Verification (Soft Version)

DealShaq may request disposal videos from consumers to help verify refund requests. Retailers must cooperate with any related information requests.

## 6. Recall Obligations

Retailers must notify DealShaq immediately upon learning of a recall, provide affected product lists, provide batch or lot information, and cooperate fully with recall investigations. DealShaq will notify consumers and issue refunds for recalled items. Retailers must not communicate directly with consumers regarding recalls.

## 7. Contact

${ENTITY_NAME}

11654 Plaza America Drive, #366

Reston, VA 20190 U.S.A.

${SUPPORT_EMAIL}`,
  },
};

export const getLegalDoc = (id) => LEGAL_DOCS[id];
